// =============================================================================
// @oekopunktemarkt/entity-pointers — canonical entity pointers + link
// vocabulary (single source of truth for both apps).
//
// Spec: knowledgebase/architecture/ARCH-029-entity-pointers.md
// Storage: public.entity_links (supabase/migrations/20260618000000_entity_links.sql)
//
// A POINTER is a URI that uniquely identifies an entity in either app
// (marketplace on oekopunktemarkt.de, LandVest on landvest.eu) or in an
// external system (ClickUp, email). Pointers are IDENTIFIERS FIRST —
// dereferenceability is optional (ClickUp URLs aren't dereferenceable by us
// either). String equality on canonical pointers IS identity equality, which
// is what makes a UNIQUE constraint on a pointer column a map.
//
// Three classes:
//   A  — first-party UUID entities:  https://<host>/api/<resource>/<uuid>
//   A′ — composite-key virtual entities (flächen groups only):
//        https://landvest.eu/api/datasets/flaechen/<seg(ds)>/groups/<seg(key)>
//   B  — external systems: https://app.clickup.com/t/<id>  |  mid:<message-id>
//   C  — opaque fallback:  urn:landvest:<kind>:<seg(id)>  (for identities that
//        cannot honestly be mapped — never mint a false first-party pointer)
//
// Canonicalization policy:
//   • First-party (A/A′): STRICT — builders throw on bad input and lowercase
//     UUIDs; parsers reject anything non-byte-canonical. Never normalize on
//     read (a DB UNIQUE constraint compares strings).
//   • External (B): MINIMAL — preserve id bytes/case verbatim (ClickUp custom
//     ids are case-sensitive; message-ids are opaque); normalize only the
//     wrapper (scheme/host casing, surrounding <> on message-ids).
//   • seg() is byte-verbatim: NO trim, NO case-fold, NO NFC/NFD normalization
//     — composite keys (e.g. flächen group_keys) come from LLM extraction and
//     must round-trip byte-exact or pointer↔row lookups break.
//
// Hosts are CONSTANTS. Never mint pointers from request origins — preview
// deployments would otherwise mint *.vercel.app pointers that never match
// anything. ONE exception, `MONDAY_HOST`: it is the only host we do not own
// (Monday mints it from the account slug), so it reads an explicitly-set env var
// with the constant as its default. See its doc for what must move with it.
//
// Dependency-free; safe in browser, server, and edge runtimes.
// =============================================================================

export const OPM_HOST = "https://oekopunktemarkt.de";
export const LANDVEST_HOST = "https://landvest.eu";
export const CLICKUP_TASK_BASE = "https://app.clickup.com/t";
/** The GREENZERO Monday account — shared across the group, so ÖPM items live there too. */
export const DEFAULT_MONDAY_HOST = "https://landvest-eu.monday.com";

/**
 * Monday account host — the ONE host that is configurable, via `MONDAY_HOST`.
 *
 * It is the exception to the header's "hosts are constants" rule because it is
 * the only host we do not own: Monday mints it from the account slug, so a
 * workspace rename or a move to another account changes it without any deploy of
 * ours. The rule's actual hazard — preview deployments minting `*.vercel.app`
 * pointers from a request origin — does not apply, because this is read from a
 * deliberately-set variable, not derived from whatever host served the request.
 *
 * THREE THINGS MUST MOVE TOGETHER if you change it:
 *   1. this env var,
 *   2. the `entity_links_source_prefix` / `_target_prefix` CHECK constraints,
 *      which pin the literal host (migration `20260731170715`) — a pointer to an
 *      unlisted host is REJECTED at write time, so the link silently never
 *      stores and the next push creates a duplicate Monday item,
 *   3. every pointer ALREADY STORED under the old host — they go invisible, they
 *      do not merely 404. `MONDAY_ITEM_RE` and the `findLinkedTarget` host
 *      filter are both built from this value, so an old-host row stops matching
 *      on both paths: the push reads "no link yet" and CREATES A SECOND Monday
 *      item for a deal that already has one. Changing the host therefore means
 *      rewriting the existing `entity_links` rows in the same migration that
 *      widens the CHECK, not just flipping the variable.
 *
 * Read at module load, and only meaningfully on the server: bundlers replace
 * `process.env.MONDAY_HOST` with `undefined` in client code (it is not
 * `NEXT_PUBLIC_`), so the browser always gets the default. That is correct here
 * — client components import this package for `opm:` / `landvest:` pointers and
 * never mint Monday ones.
 */
export const MONDAY_HOST = readHostEnv("MONDAY_HOST") ?? DEFAULT_MONDAY_HOST;

/**
 * A host from the environment, or undefined. Trailing slashes are trimmed so
 * `https://x.monday.com/` and `https://x.monday.com` cannot mint two spellings
 * of the same pointer. Guarded for runtimes with no `process` at all.
 */
function readHostEnv(name: string): string | undefined {
  const raw = typeof process !== "undefined" && process.env ? process.env[name]?.trim() : undefined;
  if (!raw) return undefined;
  return raw.replace(/\/+$/, "");
}

// -----------------------------------------------------------------------------
// Link vocabulary — `source` is the record that physically carries the
// evidence for the link; `target` is what it references. (This coincides with
// FK direction and with "derived/younger points at origin/older".)
// -----------------------------------------------------------------------------

export const LINK_RELATIONS = [
  /** computed output → its input (analysis → flächen row / group) */
  "derived_from",
  /** stored record → the raw file/batch it was loaded from (version → file) */
  "imported_from",
  /** ingested file → the email message it arrived in (mid:) */
  "received_in",
  /** first-party record → external workflow item (flächen row → ClickUp) */
  "tracked_in",
  /** record → user; only ever written with provenance 'manual_review' */
  "submitted_by",
  /** alias edge: secondary/legacy pointer form → canonical pointer */
  "same_as",
  /** sync inventory record → its source entity (reserved for the ÖPM sync) */
  "mirrors",
  /** generic sync derivation (reserved for the ÖPM sync) */
  "synced_from",
] as const;

export type LinkRelation = (typeof LINK_RELATIONS)[number];

// -----------------------------------------------------------------------------
// Prefix whitelist — kept in lock-step with the prefix CHECK on
// public.entity_links (see the migration). Adding an external SYSTEM is a rare
// one-line change here + one migration; adding a first-party RESOURCE is
// TS-only (the DB only checks prefixes).
// -----------------------------------------------------------------------------

export const POINTER_PREFIXES = [
  `${OPM_HOST}/api/`,
  `${LANDVEST_HOST}/api/`,
  `${CLICKUP_TASK_BASE}/`,
  `${MONDAY_HOST}/boards/`,
  "mid:",
  "urn:landvest:",
] as const;

/** Structural shape every stored pointer must satisfy (mirrors the structural
 *  CHECK in SQL): a URI scheme, then printable non-whitespace ASCII. */
export const STRUCTURAL_POINTER_REGEX = /^[a-z][a-z0-9+.-]*:[!-~]+$/;

export function hasKnownPrefix(uri: string): boolean {
  return POINTER_PREFIXES.some((p) => uri.startsWith(p));
}

// -----------------------------------------------------------------------------
// seg() — the one true encoder for composite-key path segments.
// Byte-verbatim UTF-8 percent-encoding, uppercase hex, RFC 3986 unreserved
// ([A-Za-z0-9._~-]) kept literal. Never decode-then-re-encode an input.
// -----------------------------------------------------------------------------

const UNRESERVED = /[A-Za-z0-9._~-]/;

export function seg(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let out = "";
  for (const b of bytes) {
    const ch = String.fromCharCode(b);
    if (b < 0x80 && UNRESERVED.test(ch)) {
      out += ch;
    } else {
      out += "%" + b.toString(16).toUpperCase().padStart(2, "0");
    }
  }
  return out;
}

/** Inverse of seg(). Throws on malformed percent sequences. */
export function unseg(value: string): string {
  return decodeURIComponent(value);
}

/** True iff `value` is exactly what seg() would produce for its decoding —
 *  i.e. canonical (uppercase hex, nothing encoded that needn't be). */
function isCanonicalSeg(value: string): boolean {
  try {
    return seg(unseg(value)) === value;
  } catch {
    return false;
  }
}

// -----------------------------------------------------------------------------
// UUIDs — lowercase canonical; builders accept any case and lowercase.
// -----------------------------------------------------------------------------

const UUID_CANONICAL = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const UUID_ANY_CASE = new RegExp(UUID_CANONICAL.source, "i");

/**
 * Whether `id` is a UUID this package will accept in a pointer.
 *
 * Exported so a CALLER can ask before building, instead of predicting the rule
 * with a second regex of its own. That drift is not hypothetical: the CRM wrote
 * a stricter pattern (pinning the version and variant nibbles) and would have
 * refused ids `canonicalUuid` accepts — a false rejection, surfaced to a rep as
 * a note claiming their data was unreadable.
 */
export function isCanonicalUuid(id: unknown): id is string {
  return typeof id === "string" && UUID_ANY_CASE.test(id);
}

function canonicalUuid(id: string, context: string): string {
  if (typeof id !== "string" || !UUID_ANY_CASE.test(id)) {
    throw new Error(`entity-pointers: '${id}' is not an RFC-4122 UUID (${context})`);
  }
  return id.toLowerCase();
}

// -----------------------------------------------------------------------------
// Class A — first-party UUID resource registry.
// `path` is the resource segment under https://<host>/api/. Multi-segment
// paths are matched before shorter ones at parse time (registration order
// below is longest-first per host).
// -----------------------------------------------------------------------------

export type EntitySystem = "opm" | "landvest" | "clickup" | "monday" | "email" | "urn" | "unknown";

interface FirstPartyResource {
  /** Discriminator, e.g. "opm:offering", "landvest:analyse". */
  type: string;
  host: string;
  path: string;
}

const FIRST_PARTY_RESOURCES: readonly FirstPartyResource[] = [
  // landvest — longest paths first so prefix matching is unambiguous
  { type: "landvest:flaechen_version", host: LANDVEST_HOST, path: "datasets/flaechen/versions" },
  { type: "landvest:flaechen_row", host: LANDVEST_HOST, path: "datasets/flaechen/rows" },
  { type: "landvest:ingested_file", host: LANDVEST_HOST, path: "ingest/files" },
  { type: "landvest:analyse", host: LANDVEST_HOST, path: "analyse" },
  // The expert's capability record for a set of Flurstücke (ARCH-043). It is
  // the SOURCE of a marketplace supply announcement, so both sides need an
  // identity: "which announcement is this declaration?" is answered the same
  // way "which Monday item is this deal?" is.
  { type: "landvest:supply_declaration", host: LANDVEST_HOST, path: "supply-declarations" },
  // marketplace
  { type: "opm:offering", host: OPM_HOST, path: "offerings" },
  { type: "opm:request", host: OPM_HOST, path: "requests" },
  { type: "opm:deal", host: OPM_HOST, path: "deals" },
  { type: "opm:land_check", host: OPM_HOST, path: "land-check" },
  // The marketplace side of ARCH-043: a LandVest capability made visible
  // without an offering. No migration was needed — the `entity_links` CHECK
  // matches the host PREFIX, not an enumerated type list.
  { type: "opm:supply_announcement", host: OPM_HOST, path: "supply-announcements" },
  { type: "opm:user", host: OPM_HOST, path: "users" },
  { type: "opm:organization", host: OPM_HOST, path: "organizations" },
  // Lead-capture artefacts. Both are pushed to the sales CRM as leads
  // (ARCH-040), so each needs an identity of its own in `entity_links` —
  // "which Monday item is this callback?" is answered the same way it is for a
  // request. No migration was needed: the `entity_links` CHECK matches the
  // `https://oekopunktemarkt.de/api/` PREFIX, not an enumerated type list.
  { type: "opm:chat_callback", host: OPM_HOST, path: "chat-callbacks" },
  { type: "opm:contact_submission", host: OPM_HOST, path: "contact-submissions" },
] as const;

function buildFirstParty(type: string, id: string): string {
  const res = FIRST_PARTY_RESOURCES.find((r) => r.type === type);
  if (!res) throw new Error(`entity-pointers: unknown resource type '${type}'`);
  return `${res.host}/api/${res.path}/${canonicalUuid(id, type)}`;
}

// Class A′ — flächen groups: virtual entities keyed (dataset_id, group_key),
// no table row, hence no UUID. NOTE: dataset ids "versions" and "rows" are
// reserved (they would collide with the Class A paths above; parse order
// resolves it, but builders reject them outright).
const RESERVED_DATASET_IDS = new Set(["versions", "rows"]);

function buildFlaechenGroup(datasetId: string, groupKey: string): string {
  if (!datasetId || RESERVED_DATASET_IDS.has(datasetId)) {
    throw new Error(`entity-pointers: invalid dataset id '${datasetId}'`);
  }
  if (!groupKey) throw new Error("entity-pointers: empty group key");
  return `${LANDVEST_HOST}/api/datasets/flaechen/${seg(datasetId)}/groups/${seg(groupKey)}`;
}

// -----------------------------------------------------------------------------
// Class B — external systems.
// -----------------------------------------------------------------------------

// ClickUp ids: global ids are lowercase base36 (e.g. 869d3z0g5); custom ids
// can be mixed-case like ABC-123. Preserve case; bound the charset.
const CLICKUP_ID = /^[A-Za-z0-9-]{4,32}$/;

function buildClickupTask(id: string): string {
  if (typeof id !== "string" || !CLICKUP_ID.test(id)) {
    throw new Error(`entity-pointers: '${id}' is not a ClickUp task id`);
  }
  return `${CLICKUP_TASK_BASE}/${id}`;
}

/** Extract a canonical ClickUp task pointer from a raw id, a /t/<id> URL, or
 *  a team-scoped /t/<teamId>/<customId> URL (kept team-scoped — a custom id
 *  alone is not globally unique). Returns null when unrecognizable. */
export function normalizeClickupRef(raw: string): string | null {
  const value = raw.trim();
  if (CLICKUP_ID.test(value)) return `${CLICKUP_TASK_BASE}/${value}`;
  const m = /^https?:\/\/app\.clickup\.com\/t\/([A-Za-z0-9-]+)(?:\/([A-Za-z0-9-]+))?\/?$/.exec(
    value
  );
  if (!m) return null;
  if (!CLICKUP_ID.test(m[1]) || (m[2] !== undefined && !CLICKUP_ID.test(m[2]))) return null;
  return m[2] ? `${CLICKUP_TASK_BASE}/${m[1]}/${m[2]}` : `${CLICKUP_TASK_BASE}/${m[1]}`;
}

// Monday items. Monday calls the item id a "Pulse ID"; it is numeric and unique
// ACROSS boards, which is why it alone is the identity — the board segment is
// only there to make the URL openable.
const MONDAY_ID = /^[0-9]{1,20}$/;

function buildMondayItem(boardId: string, pulseId: string): string {
  if (typeof boardId !== "string" || !MONDAY_ID.test(boardId)) {
    throw new Error(`entity-pointers: '${boardId}' is not a Monday board id`);
  }
  if (typeof pulseId !== "string" || !MONDAY_ID.test(pulseId)) {
    throw new Error(`entity-pointers: '${pulseId}' is not a Monday pulse id`);
  }
  return `${MONDAY_HOST}/boards/${boardId}/pulses/${pulseId}`;
}

// Email messages: RFC 2392 `mid:` URIs. The message-id core (without <>) is
// percent-encoded byte-verbatim except characters common in message-ids.
const MID_LITERAL = /[A-Za-z0-9._~@+-]/;

function midEncode(core: string): string {
  const bytes = new TextEncoder().encode(core);
  let out = "";
  for (const b of bytes) {
    const ch = String.fromCharCode(b);
    if (b < 0x80 && MID_LITERAL.test(ch)) out += ch;
    else out += "%" + b.toString(16).toUpperCase().padStart(2, "0");
  }
  return out;
}

/** Build a mid: pointer from a raw Message-ID. Strips ONE pair of surrounding
 *  angle brackets; legacy attachment suffixes (#att0 / #eml) must be stripped
 *  by the caller (they belong in link metadata, not in identity). */
function buildEmailMessage(messageId: string): string {
  const core = messageId.trim().replace(/^<([\s\S]*)>$/, "$1");
  if (!core) throw new Error("entity-pointers: empty Message-ID");
  return `mid:${midEncode(core)}`;
}

/** Split a stored ingest source_identifier into the Message-ID core and the
 *  legacy suffix ("att0", "eml", …) if one is present. */
export function splitMessageIdentifier(sourceIdentifier: string): {
  messageId: string;
  suffix: string | null;
} {
  const m = /^([\s\S]*?)#(att\d+|eml)$/.exec(sourceIdentifier.trim());
  if (m) return { messageId: m[1], suffix: m[2] };
  return { messageId: sourceIdentifier.trim(), suffix: null };
}

// Class C — opaque fallback for identities we must record but cannot honestly
// map to a first-party or known external entity (e.g. import batches that are
// not ingested-file ids). Never mint a false first-party pointer instead.
const URN_KIND = /^[a-z][a-z0-9_-]{1,63}$/;

function buildUrn(kind: string, id: string): string {
  if (!URN_KIND.test(kind)) throw new Error(`entity-pointers: invalid urn kind '${kind}'`);
  if (!id) throw new Error("entity-pointers: empty urn id");
  return `urn:landvest:${kind}:${seg(id)}`;
}

// -----------------------------------------------------------------------------
// Public builders
// -----------------------------------------------------------------------------

export const Pointers = {
  opm: {
    offering: (id: string) => buildFirstParty("opm:offering", id),
    request: (id: string) => buildFirstParty("opm:request", id),
    deal: (id: string) => buildFirstParty("opm:deal", id),
    landCheck: (id: string) => buildFirstParty("opm:land_check", id),
    user: (id: string) => buildFirstParty("opm:user", id),
    organization: (id: string) => buildFirstParty("opm:organization", id),
    supplyAnnouncement: (id: string) => buildFirstParty("opm:supply_announcement", id),
    chatCallback: (id: string) => buildFirstParty("opm:chat_callback", id),
    contactSubmission: (id: string) => buildFirstParty("opm:contact_submission", id),
  },
  landvest: {
    analyse: (id: string) => buildFirstParty("landvest:analyse", id),
    supplyDeclaration: (id: string) => buildFirstParty("landvest:supply_declaration", id),
    ingestedFile: (id: string) => buildFirstParty("landvest:ingested_file", id),
    flaechenVersion: (id: string) => buildFirstParty("landvest:flaechen_version", id),
    /** Rows are addressed by their surrogate row_uid (composite row_keys
     *  contain '/', pipes and LLM-extracted unicode — not URI-safe). */
    flaechenRow: (rowUid: string) => buildFirstParty("landvest:flaechen_row", rowUid),
    flaechenGroup: (datasetId: string, groupKey: string) => buildFlaechenGroup(datasetId, groupKey),
  },
  clickup: {
    task: (id: string) => buildClickupTask(id),
  },
  monday: {
    /** `item(boardId, pulseId)` — the pulse id is the identity; see MONDAY_HOST. */
    item: (boardId: string, pulseId: string) => buildMondayItem(boardId, pulseId),
  },
  email: {
    message: (messageId: string) => buildEmailMessage(messageId),
  },
  urn: {
    opaque: (kind: string, id: string) => buildUrn(kind, id),
  },
} as const;

// -----------------------------------------------------------------------------
// Parser — strict, canonical-only for first-party; minimal for externals.
// -----------------------------------------------------------------------------

export interface ParsedPointer {
  /** The input, byte-identical (parsing never normalizes). */
  uri: string;
  system: EntitySystem;
  /** e.g. "opm:offering", "landvest:flaechen_group", "clickup:task",
   *  "email:message", "urn:<kind>"; null when system === "unknown". */
  entityType: string | null;
  /** Primary identifier: uuid / ClickUp path id / decoded message-id core /
   *  decoded group_key / decoded urn id; null when unknown. */
  id: string | null;
  /** Extra decoded parts, e.g. { datasetId, groupKey } for flächen groups,
   *  { teamId } for team-scoped ClickUp tasks. */
  parts?: Record<string, string>;
}

const UNKNOWN = (uri: string): ParsedPointer => ({
  uri,
  system: "unknown",
  entityType: null,
  id: null,
});

/** Escape a literal string for embedding in a RegExp source. */
function reEscape(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
}

const FLAECHEN_GROUP_RE = new RegExp(
  `^${reEscape(LANDVEST_HOST)}\\/api\\/datasets\\/flaechen\\/([A-Za-z0-9._~%-]+)\\/groups\\/([A-Za-z0-9._~%-]+)$`
);
const MID_RE = /^mid:([A-Za-z0-9._~@+%-]+)$/;
const URN_RE = /^urn:landvest:([a-z][a-z0-9_-]{1,63}):([A-Za-z0-9._~%-]+)$/;
const CLICKUP_RE = new RegExp(
  `^${reEscape(CLICKUP_TASK_BASE)}\\/([A-Za-z0-9-]{4,32})(?:\\/([A-Za-z0-9-]{4,32}))?$`
);
const MONDAY_ITEM_RE = new RegExp(
  `^${reEscape(MONDAY_HOST)}\\/boards\\/([0-9]{1,20})\\/pulses\\/([0-9]{1,20})$`
);

export function parsePointer(uri: string): ParsedPointer {
  if (typeof uri !== "string" || !STRUCTURAL_POINTER_REGEX.test(uri)) return UNKNOWN(uri);

  // Class A — first-party UUID resources (registry is longest-path-first).
  for (const res of FIRST_PARTY_RESOURCES) {
    const prefix = `${res.host}/api/${res.path}/`;
    if (uri.startsWith(prefix)) {
      const rest = uri.slice(prefix.length);
      if (UUID_CANONICAL.test(rest)) {
        const [system] = res.type.split(":");
        return { uri, system: system as EntitySystem, entityType: res.type, id: rest };
      }
      return UNKNOWN(uri); // right resource, non-canonical id — reject
    }
  }

  // Class A′ — flächen groups (after Class A so .../flaechen/versions|rows win).
  const g = FLAECHEN_GROUP_RE.exec(uri);
  if (g && isCanonicalSeg(g[1]) && isCanonicalSeg(g[2])) {
    const datasetId = unseg(g[1]);
    const groupKey = unseg(g[2]);
    return {
      uri,
      system: "landvest",
      entityType: "landvest:flaechen_group",
      id: groupKey,
      parts: { datasetId, groupKey },
    };
  }

  // Class B — ClickUp.
  const cu = CLICKUP_RE.exec(uri);
  if (cu) {
    return cu[2]
      ? {
          uri,
          system: "clickup",
          entityType: "clickup:task",
          id: cu[2],
          parts: { teamId: cu[1] },
        }
      : { uri, system: "clickup", entityType: "clickup:task", id: cu[1] };
  }

  // Class B — Monday. `id` is the pulse id (unique across boards); the board is
  // carried in `parts` because it only locates the URL, not the item.
  const md = MONDAY_ITEM_RE.exec(uri);
  if (md) {
    return {
      uri,
      system: "monday",
      entityType: "monday:item",
      id: md[2],
      parts: { boardId: md[1] },
    };
  }

  // Class B — email message.
  const mid = MID_RE.exec(uri);
  if (mid && midEncode(unsafeDecode(mid[1])) === mid[1]) {
    return { uri, system: "email", entityType: "email:message", id: unsafeDecode(mid[1]) };
  }

  // Class C — opaque urn.
  const urn = URN_RE.exec(uri);
  if (urn && isCanonicalSeg(urn[2])) {
    return { uri, system: "urn", entityType: `urn:${urn[1]}`, id: unseg(urn[2]) };
  }

  return UNKNOWN(uri);
}

function unsafeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return " "; // never re-encodes to the input → canonical check fails
  }
}

/** True iff `uri` parses to a known entity AND is byte-canonical. */
export function isPointer(uri: string): boolean {
  return parsePointer(uri).system !== "unknown";
}

/**
 * Best-effort canonicalization for INPUT HANDLING ONLY (UI paste, imports):
 * trims, then if the result parses it is returned as-is; known sloppy forms
 * (http://, www., trailing slash on first-party/ClickUp) are repaired ONCE
 * and re-parsed. Returns null when nothing canonical can be made. Stored
 * pointers must always come from builders — never from this function alone.
 */
export function canonicalizePointer(raw: string): string | null {
  let uri = raw.trim();
  if (isPointer(uri)) return uri;
  uri = uri
    .replace(/^http:\/\//, "https://")
    .replace(/^https:\/\/www\./, "https://")
    .replace(/\/+$/, "");
  // lowercase scheme+host only (never the path)
  const m = /^(https:\/\/[^/]+)(\/.*)?$/.exec(uri);
  if (m) uri = m[1].toLowerCase() + (m[2] ?? "");
  return isPointer(uri) ? uri : null;
}
